import React, { memo, useEffect, useRef, useState } from "react";
import styles from "./EditProfile.module.scss";
import Modal from "../Modal/Modal";
import { LeftArrowIcon } from "../../../../assets/icons/svgicons";
import Upload from "../../Formik/Upload/Upload";
import Button from "../../Button/Button";
import InputFormik from "../../Formik/Input/InputFormik";
import { Col, Form, Row } from "react-bootstrap";
import Select from "../../Formik/Select/Select";
import Checkbox from "../../Formik/Checkbox/Checkbox";
import * as Yup from "yup";
import { validationMessage } from "../../../../Helper/helperfunction";
import { useFormik } from "formik";
import { FORMIK_REGEX, Messages } from "../../../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  updateUserAction,
  uploadProfileAction,
} from "../../../../features/user/userSlice";
import csc from "country-state-city";
import { blobToBase64 } from "../../../../utils/utils";
import ErrorText from "../../Formik/ErrorText/ErrorText";

const EditProfile = ({ show, handleClose, userData, getUserProfile }) => {
  const dispatch = useDispatch();
  const rowRef = useRef();

  const [sameAddress, setSameAddress] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({});
  const [billingAddress, setBillingAddress] = useState({});
  const [uploadImage, setUploadImage] = useState(undefined);
  const countries = csc.getAllCountries();
  const [imageSizeError, setImageSizeError] = useState(null);
  const updatedCountries = countries.map((country) => ({
    label: country.name,
    value: country.id,
    ...country,
  }));

  const updatedStates = (countryId) =>
    csc
      .getStatesOfCountry(countryId)
      .map((state) => ({ label: state.name, value: state.id, ...state }));

  const shipValidationSchema = {
    firstName: Yup.string()
      .label("First Name")
      .required(validationMessage("First Name"))
      .min(1)
      .matches(/^\S*$/, "Spaces are not allowed")
      .matches(FORMIK_REGEX.ALPHA, Messages?.ALPHABETSVALIDATION),
    lastName: Yup.string()
      .label("Last Name")
      .required(validationMessage("Last Name"))
      .min(1)
  .matches(/^\S(.*\S)?$/, "Space at start or end is not allowed")
      .matches(FORMIK_REGEX.ALPHA, Messages?.ALPHABETSVALIDATION),
    shipAddress: Yup.string()
      .label("Address")
      .required(validationMessage("Address"))
  .matches(/^\S(.*\S)?$/, "Space at start or end is not allowed")
      .min(4),
    shipZipCode: Yup.string()
      .label("Zip Code")
      .required(validationMessage("Zip Code"))
      .matches(/^\S*$/, "Spaces are not allowed")
      .min(4)
      .matches(FORMIK_REGEX.NUMBER, Messages?.NUMBERVALIDATION),
    shipCity: Yup.string()
      .label("City")
      .matches(/^\S*$/, "Spaces are not allowed")
      .required(validationMessage("City"))
      .min(4)
      .matches(FORMIK_REGEX.ALPHASPACE, Messages?.ALPHABETSVALIDATION),
    shipState: Yup.object().required(validationMessage("State")),
    shipCountry: Yup.object().required(validationMessage("Country")),
  };

  const billingValidationSchema = {
    billingAddress: Yup.string()
      .label("Address")
      .required(validationMessage("Address"))
      .matches(/^\S*$/, "Spaces are not allowed")
      .min(4),
    billingZipCode: Yup.string()
      .label("Zip Code")
      .required(validationMessage("Zip Code"))
      .min(4)
      .matches(FORMIK_REGEX.NUMBER, Messages?.NUMBERVALIDATION),
    billingCity: Yup.string()
      .label("City")
      .required(validationMessage("City"))
      .matches(/^\S*$/, "Spaces are not allowed")
      .min(4)
      .matches(FORMIK_REGEX.ALPHASPACE, Messages?.ALPHABETSVALIDATION),
    billingState: Yup.object().required(validationMessage("State")),
    billingCountry: Yup.object().required(validationMessage("Country")),
  };

  let validationSchema;
  if (sameAddress) {
    validationSchema = Yup.object({
      ...shipValidationSchema,
      ...billingValidationSchema,
    });
  } else {
    validationSchema = Yup.object(shipValidationSchema);
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: userData?.first_name,
      lastName: userData?.last_name,
      shipAddress: shippingAddress?.address,
      shipZipCode: shippingAddress?.zip_code,
      shipCity: shippingAddress?.city,
      shipState: shippingAddress?.state
        ? JSON.parse(shippingAddress?.state)
        : null,
      shipCountry: shippingAddress?.country
        ? JSON.parse(shippingAddress?.country)
        : null,
      billingAddress: billingAddress?.address,
      billingZipCode: billingAddress?.zip_code,
      billingCity: billingAddress?.city,
      billingState: billingAddress?.state
        ? JSON.parse(billingAddress?.state)
        : null,
      billingCountry: billingAddress?.country
        ? JSON.parse(billingAddress?.country)
        : null,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const dataObj = {
        firstName: values.firstName,
        lastName: values.lastName,
        address: values.shipAddress,
        zipCode: values.shipZipCode,
        city: values.shipCity,
        state: JSON.stringify(values.shipState),
        country: JSON.stringify(values.shipCountry),
        isBillingAddress: sameAddress,
      };

      let userObj;
      if (sameAddress) {
        let billingAddressData = {
          address: values.billingAddress,
          zipCode: values.billingZipCode,
          city: values.billingCity,
          state: JSON.stringify(values.billingState),
          country: JSON.stringify(values.billingCountry),
        };

        userObj = {
          ...dataObj,
          billingAddress: billingAddressData,
        };
      } else {
        userObj = { ...dataObj };
      }

      try {
        const response = await dispatch(updateUserAction(userObj));
        if (response?.status === 200) {
          handleClose();
          await getUserProfile();
        }
      } catch (error) { }
    },
  });

  useEffect(() => {
    if (userData?.addressList?.length === 2) {
      setSameAddress(true);
      setShippingAddress(userData?.addressList[0]);
      setBillingAddress(userData?.addressList[1]);
    } else if (userData?.addressList?.length === 1) {
      setShippingAddress(userData?.addressList[0]);
    }
  }, [userData]);

  const uploadImageFn = async () => {
    if (uploadImage === undefined) {
      return;
    }

    if (uploadImage && uploadImage.size) {
      const maxSize = 5 * 1024 * 1024; // 5 MB limit
      const acceptedImageTypes = [
        "image/png",
        "image/jpeg",
        "image/svg",
        "image/jpg",
      ];
      if (!acceptedImageTypes.includes(uploadImage.type)) {
        setImageSizeError(
          "Please select a valid image (PNG or JPEG or SVG OR JPG)."
        );
        return;
      }
      if (uploadImage.size > maxSize) {
        setImageSizeError(
          "Image size is too large (max 5 MB). Please select a smaller image or compress it."
        );
        return;
      }
      setImageSizeError(null);
      const base64 = await blobToBase64(uploadImage);
      const dataObj = {
        profile: base64,
      };
      try {
        await dispatch(uploadProfileAction(dataObj));
        await getUserProfile();
      } catch (error) {
        console.log("Something went wrong: ", error);
      }
    } else if (uploadImage === null && userData?.profile_pic) {
      // Handle image removal
      const dataObj = {
        profile: "",
        isProfilePicRemove: true,
      };
      try {
        await dispatch(uploadProfileAction(dataObj));
        await getUserProfile();
      } catch (error) {
        console.log("Something went wrong during removal: ", error);
      }
    }
  };

  useEffect(() => {
    uploadImageFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadImage]);

  return (
    <Modal
      show={show}
      handleClose={handleClose}
      title={
        <span className='btnback' onClick={handleClose}>
          <LeftArrowIcon />
          Back
        </span>
      }
      hideCloseBtn
      className={styles.edit_profile}
    >
      <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        <Row ref={rowRef}>
          <Col md={12}>
            <Upload
              name="profile_img"
              label="Profile Image"
              formik={formik}
              image={
                userData?.profile_pic
                  ? userData?.profile_url + "" + userData?.profile_pic
                  : null
              }
              setBlobImage={setUploadImage}
            />
            {imageSizeError && (
              <ErrorText className={styles.error}>{imageSizeError}</ErrorText>
            )}
          </Col>
          <Col md={12}>
            <InputFormik
              name="firstName"
              label="First name"
              control="input"
              type="text"
              className={styles.input}
              defaultValue={userData.first_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Please enter your first name"
              formik={formik}
              maxLength={30}
            />
          </Col>
          <Col md={12}>
            <InputFormik
              name="lastName"
              label="Last name"
              control="input"
              type="text"
              className={styles.input}
              defaultValue={userData.last_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Please enter your first name"
              formik={formik}
              maxLength={30}
            />
          </Col>
          <Col md={12}>
            <InputFormik
              name="shipAddress"
              label="Address"
              placeholder="Please enter your Address"
              control="input"
              type="text"
              className={styles.input}
              defaultValue={shippingAddress.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              formik={formik}
              maxLength={30}
            />
          </Col>
          <Col md={12}>
            <InputFormik
              name="shipZipCode"
              label="Zip Code"
              placeholder="Please enter your area zip code"
              control="input"
              type="text"
              className={styles.input}
              defaultValue={shippingAddress.zip_code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              formik={formik}
              maxLength={30}
            />
          </Col>
          <Col md={6}>
            <InputFormik
              name="shipCity"
              label="City"
              placeholder="Please enter your City"
              control="input"
              type="text"
              className={styles.input}
              defaultValue={shippingAddress.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              formik={formik}
              maxLength={30}
            />
          </Col>
          {/* <Col md={6}>
            <Select
              options={updatedCountries}
              name="shipCountry"
              label="Country"
              variant="large_height"
              placeholder={<h6>Select your country</h6>}
              className={styles.select}
              value={formik.values.shipCountry}
              formik={formik}
              onChange={(value) => {
                formik.setValues(
                  {
                    ...formik.values,
                    shipCountry: value,
                    shipState: null,
                  },
                  false
                );
                formik.setFieldError("shipState", "");
formik.setFieldTouched("shipState", false);
              }}
              onMenuOpen={() => {
                setTimeout(() => {
                  rowRef.current && rowRef.current.scrollTo(1000, 100000);
                }, 10);
              }}
            />
          </Col>
          <Col md={6}>
            <Select
              options={updatedStates(
                formik.values.shipCountry
                  ? formik.values.shipCountry.value
                  : null
              )}
              value={formik.values.shipState}
              name="shipState"
              label="State"
              variant="large_height"
              placeholder={<h6>Select your state</h6>}
              className={`${styles.select} z-lg`}
              onChange={(value) => {
                formik.setValues(
                  {
                    ...formik.values,
                    shipState: value,
                  },
                  false
                );
                formik.setFieldError("shipState", "");
formik.setFieldTouched("shipState", false);
              }}
              formik={formik}
              onMenuOpen={() => {
                setTimeout(() => {
                  rowRef.current && rowRef.current.scrollTo(1000, 100000);
                }, 10);
              }}
            />
          </Col>{" "} */}

          <Col md={6}>
  <Select
    options={updatedCountries}
    name="shipCountry"
    label="Country"
    variant="large_height"
    placeholder={<h6>Select your country</h6>}
    className={styles.select}
    value={formik.values.shipCountry}
    formik={formik}
    onChange={(value) => {
      formik.setFieldValue("shipCountry", value);
      formik.setFieldValue("shipState", null);

      // keep validation visible until state selected
      formik.setFieldTouched("shipState", true, true);
    }}
    onBlur={() => {
      formik.setFieldTouched("shipCountry", true);
    }}
    onMenuOpen={() => {
      setTimeout(() => {
        rowRef.current && rowRef.current.scrollTo(1000, 100000);
      }, 10);
    }}
  />
</Col>

{/* Shipping State */}
<Col md={6}>
  <Select
    options={updatedStates(
      formik.values.shipCountry
        ? formik.values.shipCountry.value
        : null
    )}
    value={formik.values.shipState}
    name="shipState"
    label="State"
    variant="large_height"
    placeholder={<h6>Select your state</h6>}
    className={`${styles.select} z-lg`}
    onChange={(value) => {
      formik.setFieldValue("shipState", value);
    }}
    onBlur={() => {
      formik.setFieldTouched("shipState", true);
    }}
    formik={formik}
    onMenuOpen={() => {
      setTimeout(() => {
        rowRef.current && rowRef.current.scrollTo(1000, 100000);
      }, 10);
    }}
  />
</Col>
          <Col md={12}>
            <Checkbox
              label="Select, if your home or business address is different than your shipping address."
              name="different_areas"
              className={styles.checkbox}
              checked={sameAddress}
              onChange={(e) => setSameAddress(e.target.checked)}
            />
          </Col>
          {sameAddress && (
            <>
              <Col md={12}>
                <InputFormik
                  name="billingAddress"
                  label="Billing Address"
                  placeholder="Please enter your Address"
                  control="input"
                  type="text"
                  className={styles.input}
                  defaultValue={billingAddress.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  formik={formik}
                  maxLength={30}
                />
              </Col>
              <Col md={12}>
                <InputFormik
                  name="billingZipCode"
                  label="Zip Code"
                  placeholder="Please enter your area zip code"
                  type="text"
                  className={styles.input}
                  defaultValue={billingAddress.zip_code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  formik={formik}
                  maxLength={30}
                />
              </Col>
              <Col md={12}>
                <InputFormik
                  name="billingCity"
                  label="City"
                  placeholder="Please enter your City"
                  type="text"
                  className={styles.input}
                  defaultValue={billingAddress.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  formik={formik}
                  maxLength={30}
                />
              </Col>
              {/* <Col md={6}>
                <Select
                  options={updatedCountries}
                  name="billingCountry"
                  label="Country"
                  variant="large_height"
                  placeholder={<h6>Select your country</h6>}
                  onMenuOpen={() => {
                    setTimeout(() => {
                      rowRef.current && rowRef.current.scrollTo(1000, 100000);
                    }, 10);
                  }}
                  className={styles.select}
                  value={formik.values.billingCountry}
                  formik={formik}
                  onChange={(value) => {
                    formik.setValues(
                      {
                        ...formik.values,
                        billingCountry: value,
                        billingState: null,
                      },
                      false
                    );
                    formik.setFieldError("billingState", "");
                    formik.setFieldTouched("billingState", false);
                  }}
                />
              </Col> */}
              {/* <Col md={6}>
                <Select
                  options={updatedStates(
                    formik.values.billingCountry
                      ? formik.values.billingCountry.value
                      : null
                  )}
                  value={formik.values.billingState}
                  name="shipState"
                  label="State"
                  variant="large_height"
                  placeholder={<h6>Select your state</h6>}
                  className={`${styles.select} z-lg`}
                  onChange={(value) => {
                    formik.setValues(
                      {
                        ...formik.values,
                        billingState: value,
                      },
                      false
                    );
                    formik.setFieldError("billingState", "");
                    formik.setFieldTouched("billingState", false);
                  }}
                  onMenuOpen={() => {
                    setTimeout(() => {
                      rowRef.current && rowRef.current.scrollTo(1000, 100000);
                    }, 10);
                  }}
                  formik={formik}
                />
              </Col> */}
              <Col md={6}>
  <Select
    options={updatedCountries}
    name="billingCountry"
    label="Country"
    variant="large_height"
    placeholder={<h6>Select your country</h6>}
    className={styles.select}
    value={formik.values.billingCountry}
    formik={formik}
    onChange={(value) => {
      formik.setFieldValue("billingCountry", value);
      formik.setFieldValue("billingState", null);

      // keep validation visible until state selected
      formik.setFieldTouched("billingState", true, true);
    }}
    onBlur={() => {
      formik.setFieldTouched("billingCountry", true);
    }}
    onMenuOpen={() => {
      setTimeout(() => {
        rowRef.current && rowRef.current.scrollTo(1000, 100000);
      }, 10);
    }}
  />
</Col>

{/* Billing State */}
<Col md={6}>
  <Select
    options={updatedStates(
      formik.values.billingCountry
        ? formik.values.billingCountry.value
        : null
    )}
    value={formik.values.billingState}
    name="billingState"
    label="State"
    variant="large_height"
    placeholder={<h6>Select your state</h6>}
    className={`${styles.select} z-lg`}
    onChange={(value) => {
      formik.setFieldValue("billingState", value);
    }}
    onBlur={() => {
      formik.setFieldTouched("billingState", true);
    }}
    formik={formik}
    onMenuOpen={() => {
      setTimeout(() => {
        rowRef.current && rowRef.current.scrollTo(1000, 100000);
      }, 10);
    }}
  />
</Col>
              
            </>
          )}
        </Row>
        <Button
          type="button"
          fluid
          className={styles.submit_btn}
          onClick={(e) => {
            formik.handleSubmit(e);
          }}
        >
          Save Profile
        </Button>
      </form>
    </Modal>
  );
};

export default memo(EditProfile);
